
import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { setUserId as setGAUserId } from "../utils/analytics";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Send cookies on every axios call (httpOnly access_token + reCAPTCHA cookies).
// Required for cross-origin requests when the backend sets credentials cookies.
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // localStorage token is kept ONLY for backwards compatibility with users
  // who logged in before the cookie migration. New logins rely on the
  // httpOnly cookie automatically attached by the browser.
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const storedToken = localStorage.getItem("token");
    const headers = storedToken ? { Authorization: `Bearer ${storedToken}` } : {};
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, { headers });
      setUser(response.data);
      if (storedToken) setToken(storedToken);
      if (response.data?.id) setGAUserId(response.data.id);
    } catch (error) {
      // No cookie AND no token -> not authenticated, this is a normal case.
      if (storedToken) localStorage.removeItem("token");
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
    // API_URL/axios are stable module-scope constants
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password, captchaToken = null) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, {
      email,
      password,
      captcha_token: captchaToken
    });
    const { token: newToken, user: userData } = response.data;
    // Keep mirroring in localStorage to support pages that still read it.
    // Once all pages stop reading localStorage we can drop this line.
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    if (userData?.id) setGAUserId(userData.id);
    return userData;
  }, []);

  const register = useCallback(async (email, password, fullName, firstName = null, lastName = null, phoneNumber = null, companyName = null, captchaToken = null) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, {
      email,
      password,
      full_name: fullName,
      first_name: firstName,
      last_name: lastName,
      phone_number: phoneNumber,
      company_name: companyName,
      captcha_token: captchaToken
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    if (userData?.id) setGAUserId(userData.id);
    return userData;
  }, []);

  const logout = useCallback(async () => {
    // Best-effort cookie clear on the server; never block the UI on it.
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {});
    } catch (_) {
      /* noop */
    }
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  }, []);

  const updateUser = useCallback((userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
